import { Button } from "@/components/ui/button";
import { 
  Check, ArrowRight, Users, Smartphone, QrCode, Link2, 
  Star, Clock, UserPlus, Phone, Mail, Globe, 
  Briefcase, Building2, Sparkles, Share2, 
  BadgeCheck, Eye, Zap, GraduationCap, Home, Calculator,
  MessageSquare, Image, FileText
} from "lucide-react";
import qcardLogo from "@/assets/qcard-logo-official.png";
import qcardHero from "@/assets/qcard-hero-new.png";
import companyLogos from "@/assets/company-logos.png";

const features = [
  { icon: Image, title: "Photo & Contact Info", description: "Professional headshot with all your details" },
  { icon: Briefcase, title: "Job Title & Bio", description: "Showcase your role and expertise" },
  { icon: Globe, title: "Social & Website Links", description: "Connect all your platforms in one place" },
  { icon: QrCode, title: "QR Code", description: "Instant sharing with a simple scan" },
  { icon: Link2, title: "Personal URL", description: "Your own qcard.me/yourname link" },
  { icon: UserPlus, title: "Instant Add-to-Contacts", description: "One tap to save you to their phone" },
  { icon: FileText, title: "Customizable Sections", description: "Tailor your profile to your needs" },
  { icon: Share2, title: "Unlimited Sharing", description: "Share as many times as you want" },
];

const benefits = [
  { icon: BadgeCheck, title: "Look Polished & Credible", description: "Make a professional first impression every time" },
  { icon: Zap, title: "Impress in Seconds", description: "Stand out with a modern, sleek digital presence" },
  { icon: Globe, title: "Works Anywhere", description: "Share via QR, link, or text — no app required" },
  { icon: UserPlus, title: "Get Saved Instantly", description: "Your contacts save you to their phone with one tap" },
];

const audiences = [
  { icon: Users, title: "Individuals", description: "Anyone who wants to network smarter" },
  { icon: Home, title: "Real Estate Agents", description: "Close deals with instant follow-up" },
  { icon: MessageSquare, title: "Consultants", description: "Build credibility from the first meeting" },
  { icon: Calculator, title: "Financial Professionals", description: "Establish trust with a polished profile" },
  { icon: Briefcase, title: "Lawyers", description: "Present a professional image to clients" },
  { icon: Building2, title: "Enterprises", description: "Equip your entire team with digital cards" },
];

const steps = [
  { step: "01", title: "Create Your Free QCard", description: "Sign up in seconds — no credit card needed" },
  { step: "02", title: "Customize Your Profile", description: "Add your photo, bio, links, and contact info" },
  { step: "03", title: "Share Instantly", description: "Use your QR code or personal link anywhere" },
];

const testimonials = [
  {
    quote: "QCard has completely changed how I network. People actually save my contact info now.",
    name: "Sarah M.",
    role: "Real Estate Agent",
  },
  {
    quote: "The instant add-to-contacts feature is a game changer. No more lost business cards.",
    name: "James T.",
    role: "Financial Advisor",
  },
  {
    quote: "I love how professional my QCard looks. It makes a great first impression.",
    name: "Emily R.",
    role: "Consultant",
  },
];

const HomepageFreeVersion1 = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Announcement Banner */}
      <div className="bg-gradient-to-r from-primary to-[hsl(12,90%,60%)] text-primary-foreground py-3 text-center">
        <p className="text-sm md:text-base font-medium">
          🎉 <span className="font-semibold">QCard is now free for everyone</span> — Create your professional digital business card today
        </p>
      </div>

      {/* Header */}
      <header className="py-4 border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <img src={qcardLogo} alt="QCard" className="h-10" />
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="hidden md:inline-flex">See Features</Button>
            <Button variant="ghost" className="hidden md:inline-flex">Schedule a Demo</Button>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
              Create Your Free QCard
            </Button>
          </div>
        </div>
      </header>

      {/* SECTION 1: Hero */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/50 via-background to-accent/30" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px]" />
        
        <div className="container relative z-10 mx-auto px-4 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6 border border-primary/20">
                <Sparkles className="w-4 h-4" />
                Now Free for Everyone
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-foreground">
                Everything You Need to Be Remembered.{" "}
                <span className="text-gradient">Free.</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                Create a polished digital business card with instant contact saving — all at no cost. 
                The same platform trusted by industry leaders, now available to everyone.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 font-semibold shadow-lg shadow-primary/30">
                  Create Your Free QCard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2">
                  See How It Works
                </Button>
              </div>
              
              <div className="flex items-center gap-6 justify-center lg:justify-start text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  <span>All features included</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  <span>60-second setup</span>
                </div>
              </div>
            </div>

            {/* Right - Hero Image */}
            <div className="relative flex justify-center">
              <img 
                src={qcardHero} 
                alt="Professionals exchanging contact info with QCard" 
                className="w-full max-w-lg rounded-2xl shadow-2xl shadow-primary/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Company Logos */}
      <section className="py-12 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <p className="text-center text-muted-foreground mb-8 text-sm font-medium">Trusted by professionals at leading companies</p>
          <img 
            src={companyLogos} 
            alt="Trusted by Redfin, Keller Williams, UBS, S&P Global, Sotheby's, Compass, and more" 
            className="max-w-4xl mx-auto w-full"
          />
        </div>
      </section>

      {/* SECTION 2: All Features. No Fees. */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              All Features. <span className="text-gradient">No Fees.</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to create a professional digital presence — included free.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground text-sm bg-muted/50 inline-flex items-center gap-2 px-6 py-3 rounded-full">
              <Check className="w-4 h-4 text-green-600" />
              No hidden costs. No premium upsells for essentials.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3: Value of Professional Digital Identity */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Your Professional <span className="text-gradient">Digital Identity</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              More than a business card — it's how you present yourself to the world.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-xl mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: How It Works */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              How It <span className="text-gradient">Works</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Get your professional digital card in under 60 seconds.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center">
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-primary/50 to-transparent" />
                )}
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-[hsl(12,90%,60%)] flex items-center justify-center mx-auto mb-6 text-primary-foreground text-3xl font-bold shadow-lg shadow-primary/30">
                  {step.step}
                </div>
                <h3 className="font-semibold text-xl mb-3">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 font-semibold shadow-lg shadow-primary/30">
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 5: Why Choose QCard */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Why Choose <span className="text-gradient">QCard?</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                In a world of forgettable first impressions, QCard helps you stand out and stay connected.
              </p>
              
              <div className="space-y-4">
                {[
                  "Be remembered — not just another card in the drawer",
                  "Be searchable — your digital profile lives online",
                  "Look professional — polished, modern, and credible",
                  "Stand out — make every interaction count",
                  "Replace outdated paper cards — go digital, go green",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Old Way vs QCard */}
            <div className="grid gap-6">
              <div className="bg-card border border-border rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                    <span className="text-destructive font-bold">✕</span>
                  </div>
                  <h3 className="font-semibold text-lg">The Old Way</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Paper cards get lost or thrown away</li>
                  <li>• Manual contact entry (if they bother)</li>
                  <li>• Outdated info after a job change</li>
                  <li>• No way to share digitally</li>
                  <li>• Forgettable first impression</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">The QCard Way</h3>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Saved directly to their phone</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>One-tap contact saving</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Always up-to-date</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Share via QR, link, or text</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Professional, memorable impression</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: Who QCard Is For */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Who Is QCard <span className="text-gradient">For?</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professionals across every industry use QCard to make lasting connections.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {audiences.map((audience, index) => (
              <div 
                key={index} 
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 hover:shadow-lg transition-all duration-300 flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <audience.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{audience.title}</h3>
                  <p className="text-sm text-muted-foreground">{audience.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground">
              <Building2 className="w-4 h-4 inline mr-2" />
              Need QCard for your team? <a href="#" className="text-primary font-semibold hover:underline">Enterprise plans are available →</a>
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 7: Free Tier Callout */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6 border border-primary/20">
              <Star className="w-4 h-4" />
              Free Tier. Full Capability.
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              This Isn't a "Basic" Version. <span className="text-gradient">It's the Real Thing.</span>
            </h2>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              We believe everyone deserves a professional digital presence. That's why QCard Free includes 
              every feature you need — no artificial limitations, no paywalls for essentials.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {["Full Profile", "Unlimited Sharing", "QR Code", "Personal URL", "Contact Saving", "All Links"].map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-10 py-6 font-semibold shadow-lg shadow-primary/30">
              Create Your Free QCard
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 8: Testimonials */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              What Professionals <span className="text-gradient">Are Saying</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-card border border-border rounded-2xl p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-primary fill-primary" />
                  ))}
                </div>
                <p className="text-lg mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: Final CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-foreground to-foreground/90 text-background">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Create Your Free QCard — It Only Takes 60 Seconds.
          </h2>
          <p className="text-xl text-background/70 mb-10 max-w-2xl mx-auto">
            No credit card required. All features included. Start making lasting impressions today.
          </p>
          
          <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground text-xl px-12 py-8 font-semibold shadow-xl shadow-primary/40">
            Create Your Free QCard
            <ArrowRight className="ml-3 h-6 w-6" />
          </Button>

          <div className="flex items-center gap-8 justify-center mt-10 text-sm text-background/60">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>60-second setup</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              <span>No credit card</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              <span>All features free</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-background/60 border-t border-background/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src={qcardLogo} alt="QCard" className="h-8 brightness-0 invert opacity-80" />
            <div className="flex gap-6 text-sm">
              <a href="#" className="hover:text-background transition-colors">Features</a>
              <a href="#" className="hover:text-background transition-colors">How It Works</a>
              <a href="#" className="hover:text-background transition-colors">Enterprise</a>
              <a href="#" className="hover:text-background transition-colors">Contact</a>
            </div>
            <p className="text-sm">© 2024 QCard. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomepageFreeVersion1;
